/**
 * EffectsGroup — a rack section that hosts the effect module panels and
 * lets the user drag-reorder, add, and remove them. The audio graph
 * follows the visual chain: any change calls EffectsChain methods, which
 * rewire voiceBus → effects → master.
 *
 * Layout per slot:
 *   ┌───────────────────────────────┐
 *   │ DIST 1 ↕            ✕         │  ← header drag handle + remove
 *   │   Drive  Tone  Mix            │
 *   └───────────────────────────────┘
 *
 * A "+ palette" row at the top of the group lets the user add any
 * effect type the registry knows about. Effects render in chain order;
 * subscribing to the chain keeps the DOM in sync after preset loads,
 * randomize, and direct add/remove calls.
 *
 * Drag implementation: each slot is `draggable`. The dataTransfer
 * payload is the *instance id* — picking a type would be ambiguous when
 * multiple instances of the same type exist in the chain.
 */

import type { EffectsChain, EffectModule } from '@ac-slayer/synth-core';
import type { EffectRegistry } from '@ac-slayer/synth-core';
import type { ModArcController } from '@ac-slayer/synth-core';
import type { MidiBindings } from '@ac-slayer/synth-midi';
import { ModulePanel } from './ModulePanel';

export interface EffectsGroupOptions {
  readonly chain: EffectsChain;
  readonly registry: EffectRegistry;
  readonly modArcs: ModArcController;
  /** Optional MIDI bindings store. When supplied, every effect knob
   *  built into the group panels gets right-click Learn / Unbind
   *  affordances. */
  readonly midiBindings?: MidiBindings;
}

interface SlotEntry {
  readonly element: HTMLElement;
  readonly title: HTMLElement;
  readonly panel: ModulePanel;
  readonly module: EffectModule;
  /** Re-read `module.bypass` and update the on/off button + slot
   *  styling. Called from syncDom so a preset load that flips the
   *  flag reflects in the UI. */
  readonly refreshBypass: () => void;
}

export class EffectsGroup {
  readonly element: HTMLElement;
  private readonly list: HTMLElement;
  private readonly emptyState: HTMLElement;
  private readonly chain: EffectsChain;
  private readonly registry: EffectRegistry;
  private readonly modArcs: ModArcController;
  private readonly midiBindings: MidiBindings | null;
  /** id → DOM bookkeeping. Reused across syncs so re-renders just move
   *  existing panels rather than rebuilding their children (which
   *  would clobber the user's open knob menus, focus state, etc.). */
  private readonly slots = new Map<string, SlotEntry>();
  private readonly unsubscribe: () => void;

  constructor(opts: EffectsGroupOptions) {
    this.chain = opts.chain;
    this.registry = opts.registry;
    this.modArcs = opts.modArcs;
    this.midiBindings = opts.midiBindings ?? null;

    this.element = document.createElement('section');
    this.element.className = 'effects-group';
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', 'Effects rack');

    const header = document.createElement('header');
    header.className = 'effects-group-header';
    const title = document.createElement('h2');
    title.className = 'effects-group-title';
    title.textContent = 'EFFECTS';
    const hint = document.createElement('span');
    hint.className = 'effects-group-hint label';
    hint.textContent = 'drag to reorder · ✕ to remove';
    header.append(title, hint);
    this.element.appendChild(header);

    // ── Palette: one button per registered effect type. Clicking adds
    // a fresh instance to the end of the chain. The palette stays
    // alive across chain mutations — duplicates ARE allowed (two delays,
    // three distortions), so we don't disable buttons even when an
    // instance of that type already exists.
    const palette = document.createElement('div');
    palette.className = 'effects-group-palette';
    const paletteLabel = document.createElement('span');
    paletteLabel.className = 'label effects-group-palette-label';
    paletteLabel.textContent = '+ ADD';
    palette.appendChild(paletteLabel);
    for (const desc of this.registry.list()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm effects-group-palette-btn';
      btn.textContent = desc.displayName;
      btn.title = `Add ${desc.displayName}`;
      btn.addEventListener('click', () => {
        this.chain.addEffect(desc.type);
      });
      palette.appendChild(btn);
    }
    this.element.appendChild(palette);

    this.list = document.createElement('div');
    this.list.className = 'effects-group-list';
    this.element.appendChild(this.list);

    // Empty-state prompt — shown when the chain has no effects so the
    // big bordered block doesn't look broken. Hidden the moment the
    // user adds anything (and re-shown on remove-down-to-zero).
    this.emptyState = document.createElement('div');
    this.emptyState.className = 'effects-group-empty';
    const headline = document.createElement('span');
    headline.className = 'effects-group-empty-headline';
    headline.textContent = 'No effects';
    const emptyHint = document.createElement('span');
    emptyHint.className = 'effects-group-empty-hint label';
    emptyHint.textContent =
      'Add an effect from the palette above to start coloring the signal.';
    this.emptyState.append(headline, emptyHint);
    this.element.appendChild(this.emptyState);

    // Initial population.
    this.syncDom();

    // Sync DOM order to chain order whenever the chain changes (e.g.
    // preset load, randomize, drag-drop, palette add). Drag-drop also
    // updates the chain, which fires this listener — the no-op check
    // in EffectsChain.setOrder prevents an infinite loop.
    this.unsubscribe = this.chain.subscribe(() => this.syncDom());
  }

  dispose(): void {
    this.unsubscribe();
    for (const slot of this.slots.values()) slot.panel.dispose();
    this.slots.clear();
  }

  /** Wire HTML5 drag events on an effect slot. */
  private wireDrag(slot: HTMLElement, id: string): void {
    slot.draggable = true;

    slot.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('application/x-effect-id', id);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      slot.classList.add('effects-group-slot-dragging');
    });
    slot.addEventListener('dragend', () => {
      slot.classList.remove('effects-group-slot-dragging');
      for (const s of this.slots.values()) {
        s.element.classList.remove('effects-group-slot-over');
      }
    });

    slot.addEventListener('dragover', (e) => {
      const t = e.dataTransfer?.types.includes('application/x-effect-id');
      if (!t) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      slot.classList.add('effects-group-slot-over');
    });
    slot.addEventListener('dragleave', () => {
      slot.classList.remove('effects-group-slot-over');
    });

    slot.addEventListener('drop', (e) => {
      e.preventDefault();
      slot.classList.remove('effects-group-slot-over');
      const sourceId = e.dataTransfer?.getData('application/x-effect-id');
      if (!sourceId || sourceId === id) return;
      this.reorderByDrop(sourceId, id);
    });
  }

  /** Move dragged effect into the dropped-on slot's neighborhood. */
  private reorderByDrop(sourceId: string, targetId: string): void {
    const order = [...this.chain.getOrder()];
    const sourceIdx = order.indexOf(sourceId);
    const targetIdx = order.indexOf(targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    order.splice(sourceIdx, 1);
    const adjustedTarget = sourceIdx < targetIdx ? targetIdx - 1 : targetIdx;
    const insertAt = sourceIdx < targetIdx ? adjustedTarget + 1 : adjustedTarget;
    order.splice(insertAt, 0, sourceId);
    this.chain.setOrder(order);
  }

  /**
   * Reflow the DOM to match the chain's current modules. Builds slots
   * for any new effects, re-uses existing slots when ids match, and
   * disposes panels whose effects were removed.
   */
  private syncDom(): void {
    const modules = this.chain.getModules();
    const stillPresent = new Map(modules.map((m) => [m.id, m]));

    // Drop slots for effects that left the chain — OR whose underlying
    // module instance was replaced (preset load via
    // `EffectsChain.applyPreset` keeps the same id but builds a fresh
    // module). A stale slot's knobs are bound to the disposed module's
    // Parameter instances, so it has to be torn down and rebuilt
    // against the new module or the user's knob movements update a
    // module that's no longer in the audio graph.
    for (const [id, slot] of this.slots) {
      const live = stillPresent.get(id);
      if (!live || live !== slot.module) {
        slot.panel.dispose();
        slot.element.remove();
        this.slots.delete(id);
      }
    }

    // Build slots for new effects, reuse existing ones for the rest.
    for (const m of modules) {
      let slot = this.slots.get(m.id);
      if (!slot) slot = this.createSlot(m);
      this.list.appendChild(slot.element); // re-appending also moves
      // Re-read bypass — preset loads mutate `module.bypass` directly,
      // so the cached slot's button text + dimmed styling can drift
      // from reality unless we refresh on every chain change.
      slot.refreshBypass();
    }

    // Refresh titles in case the count-by-type changed (going from one
    // distortion to two means both should show "DIST 1" / "DIST 2").
    this.refreshTitles();

    // Toggle the empty-state prompt. The list itself stays in the DOM
    // (kept around so its flex sizing doesn't collapse the parent),
    // just empty.
    const empty = modules.length === 0;
    this.emptyState.hidden = !empty;
    this.list.hidden = empty;
  }

  /**
   * Build a fresh panel + remove-button + drag-handle for a new effect
   * instance. The created entry is cached in `slots` keyed by id.
   */
  private createSlot(module: EffectModule): SlotEntry {
    const panel = ModulePanel.fromModule(module, {
      modArcs: this.modArcs,
      ...(this.midiBindings ? { midiBindings: this.midiBindings } : {}),
    });
    const slotEl = panel.element;
    slotEl.classList.add('effects-group-slot');

    // Add a bypass toggle and a remove (×) button into the panel header.
    // The header is the first child of the panel; we reach into it to
    // keep the visual grouping right.
    const headerEl = slotEl.querySelector('.module-panel-header');
    const titleEl =
      (headerEl?.querySelector('.module-panel-title') as HTMLElement | null) ??
      slotEl;
    let refreshBypassRef: (() => void) | null = null;
    if (headerEl) {
      // ── Bypass toggle ────────────────────────────────────────────
      // Click to switch the effect on / off. Bypassed effects route
      // around in EffectsChain.rewire so they cost nothing in the
      // audio graph. The button visibly reflects bypass state and
      // dims the panel content so it's obvious which effects are
      // currently audible.
      const bypassBtn = document.createElement('button');
      bypassBtn.type = 'button';
      bypassBtn.className = 'effects-group-bypass';
      bypassBtn.setAttribute('aria-label', `${module.displayName} on/off`);
      refreshBypassRef = (): void => {
        const off = !!module.bypass;
        bypassBtn.textContent = off ? 'OFF' : 'ON';
        bypassBtn.classList.toggle('is-off', off);
        bypassBtn.classList.toggle('is-on', !off);
        bypassBtn.setAttribute('aria-pressed', off ? 'false' : 'true');
        bypassBtn.title = off
          ? `Switch ${module.displayName} on`
          : `Switch ${module.displayName} off`;
        slotEl.classList.toggle('effects-group-slot-bypassed', off);
      };
      refreshBypassRef();
      bypassBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.chain.setBypass(module.id, !module.bypass);
        refreshBypassRef!();
      });
      bypassBtn.addEventListener('mousedown', (e) => e.stopPropagation());
      headerEl.appendChild(bypassBtn);

      // ── Remove (×) button ────────────────────────────────────────
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'effects-group-remove';
      removeBtn.textContent = '✕';
      removeBtn.title = `Remove ${module.displayName}`;
      removeBtn.setAttribute('aria-label', `Remove ${module.displayName}`);
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.chain.removeEffect(module.id);
      });
      // Suppress drag events on the button so clicking ✕ doesn't
      // start an accidental drag of the panel.
      removeBtn.addEventListener('mousedown', (e) => e.stopPropagation());
      headerEl.appendChild(removeBtn);
    }

    this.wireDrag(slotEl, module.id);

    const entry: SlotEntry = {
      element: slotEl,
      title: titleEl,
      panel,
      module,
      // refreshBypass is defined inside the headerEl branch above when
      // a header exists; in the unlikely case there's no header, fall
      // through to a no-op so syncDom can call this unconditionally.
      refreshBypass: refreshBypassRef ?? ((): void => {}),
    };
    this.slots.set(module.id, entry);
    return entry;
  }

  /**
   * Refresh slot titles so duplicates show their position number.
   * "DIST 1" / "DIST 2" when there are two; plain "DIST" when there's
   * just one. Cheap to call after every chain mutation since it just
   * rewrites textContent on the cached title nodes.
   */
  private refreshTitles(): void {
    const modules = this.chain.getModules();
    const totals = this.chain.countByType();
    const counters = new Map<string, number>();
    for (const m of modules) {
      const slot = this.slots.get(m.id);
      if (!slot) continue;
      const total = totals.get(m.type) ?? 1;
      const idx = (counters.get(m.type) ?? 0) + 1;
      counters.set(m.type, idx);
      slot.title.textContent =
        total > 1 ? `${m.displayName} ${idx}` : m.displayName;
    }
  }
}
